import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Users, FileText, Calendar, Search, TrendingUp, 
  UserPlus, FilePlus, Loader2, ChevronRight, X,
  Download, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function AdminReports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Date filters
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  
  // Stats
  const [stats, setStats] = useState({
    newUsers: 0,
    newFilings: 0,
    users: [],
    filings: []
  });
  
  // Dialog states
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showFilingsDialog, setShowFilingsDialog] = useState(false);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/admin/reports/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = () => {
    fetchStats();
  };

  const handleQuickFilter = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'agent': return 'bg-blue-100 text-blue-700';
      case 'agency': return 'bg-purple-100 text-purple-700';
      case 'business': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'in_review': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'need_info': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) return null;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Reports" user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Reports" user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6" />
              Reports & Analytics
            </h2>
            <p className="text-muted-foreground">View registration and filing statistics</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fetchStats(true)}
            disabled={refreshing}
            data-testid="reports-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Date Filter Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Date Range Filter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[180px]"
                  data-testid="reports-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[180px]"
                  data-testid="reports-end-date"
                />
              </div>
              <Button onClick={handleSearch} data-testid="reports-search-btn">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              <div className="flex gap-2 ml-4">
                <Button variant="outline" size="sm" onClick={() => handleQuickFilter(0)} data-testid="reports-today-btn">
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickFilter(7)} data-testid="reports-last7days-btn">
                  Last 7 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickFilter(30)} data-testid="reports-last30days-btn">
                  Last 30 Days
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickFilter(90)} data-testid="reports-last90days-btn">
                  Last 90 Days
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* New Users Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-primary/50"
            onClick={() => setShowUsersDialog(true)}
            data-testid="reports-users-card"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                New User Registrations
              </CardTitle>
              <div className="p-2 bg-green-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-bold text-green-600" data-testid="reports-users-count">{stats.newUsers}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {startDate === endDate ? 'Today' : `${startDate} to ${endDate}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  View Details <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* New Filings Card */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-primary/50"
            onClick={() => setShowFilingsDialog(true)}
            data-testid="reports-filings-card"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                New Tax Filings
              </CardTitle>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FilePlus className="h-5 w-5 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-4xl font-bold text-blue-600" data-testid="reports-filings-count">{stats.newFilings}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {startDate === endDate ? 'Today' : `${startDate} to ${endDate}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  View Details <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Dialog */}
        <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-green-600" />
                New User Registrations ({stats.newUsers})
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {stats.users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No new registrations in this period</p>
                </div>
              ) : (
                stats.users.map((u, idx) => (
                  <div key={u.id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">
                        {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}` : 'No Name'}
                      </p>
                      <p className="text-sm text-muted-foreground">{u.email || u.phone || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">
                        Registered: {formatDate(u.created_at)}
                      </p>
                    </div>
                    <Badge className={getRoleBadgeColor(u.role)}>{u.role}</Badge>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Filings Dialog */}
        <Dialog open={showFilingsDialog} onOpenChange={setShowFilingsDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FilePlus className="h-5 w-5 text-blue-600" />
                New Tax Filings ({stats.newFilings})
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {stats.filings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No new filings in this period</p>
                </div>
              ) : (
                stats.filings.map((f, idx) => (
                  <div key={f.id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">
                        {f.client_name || 'Unknown Client'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Tax Year: {f.tax_year} | Type: {f.filing_type || 'Individual'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Submitted: {formatDate(f.created_at)}
                      </p>
                    </div>
                    <Badge className={getStatusBadgeColor(f.status)}>{f.status}</Badge>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
